import { AuthService } from '@core/auth/auth.service';
import { AppearanceService } from '@core/appearance/appearance.service';
import { TenantLogoService } from '@core/appearance/tenant-logo.service';
import { AuthUtils } from '@core/auth/auth.utils';
import { RolesService } from '@core/api/roles/roles.service';
import { employeeRoleLabel } from 'app/modules/admin/administration/employee-management/employee-role-label';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink, RouterOutlet } from '@angular/router';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import {
    FuseNavigationItem,
    FuseNavigationService,
    FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { FuseConfigService } from '@fuse/services/config';
import { TranslocoPipe } from '@jsverse/transloco';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { NotificationsComponent } from 'app/layout/common/notifications/notifications.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { Subject, combineLatest, takeUntil, switchMap, map, catchError, of } from 'rxjs';

import { MatDialog } from '@angular/material/dialog';
import { AbsenceRequestDialogComponent } from '@core/ui/dialogs/absence-request-dialog/absence-request-dialog.component';
import { TimeTrackingBarComponent } from '../../../common/time-tracker/time-tracking-bar';

@Component({
    selector: 'classy-layout',
    templateUrl: './classy.component.html',
    styleUrl: './classy.component.scss',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        TranslocoPipe,
        FuseLoadingBarComponent,
        FuseVerticalNavigationComponent,
        NotificationsComponent,
        UserComponent,
        MatIconModule,
        MatButtonModule,
        LanguagesComponent,
        MatTooltipModule,
        RouterOutlet,
        RouterLink,
        TimeTrackingBarComponent,
    ],
})
export class ClassyLayoutComponent implements OnInit, OnDestroy {
    roleName = '';
    isDark = false;
    isScreenSmall: boolean;
    navigation: Navigation;
    user: User = {} as User;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        public appearance: AppearanceService,
        public tenantLogo: TenantLogoService,
        private _navigationService: NavigationService,
        private _userService: UserService,
        private _authService: AuthService,
        private _rolesService: RolesService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _dialog: MatDialog,
        private _fuseConfigService: FuseConfigService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        combineLatest([
            this._fuseConfigService.config$,
            this._fuseMediaWatcherService.onMediaQueryChange$(['(prefers-color-scheme: dark)']),
        ])
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(([config, media]) => {
                this.isDark =
                    config.scheme === 'dark' ||
                    (config.scheme === 'auto' && media.breakpoints['(prefers-color-scheme: dark)']);
            });
        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
                // this._attachNavigationFunctions(this.navigation.default);
                if (this.navigation?.default) {
                    this._attachNavigationFunctions(this.navigation.default);
                }
            });

        // Subscribe to the user service
        this._userService.user$
            .pipe(
                switchMap((user: User) => {

                    this.user = user;
                    this.roleName = '';
                    let roleId: string | undefined;
                    try {
                        roleId = AuthUtils.decodeToken(this._authService.accessToken)?.roleid;
                    } catch {
                        return of('');
                    }
                    if (!roleId) return of('');
                    return this._rolesService.getRoles().pipe(
                        map((roles: { id: string; name: string }[]) => {
                            const role = roles.find((item) => item.id === roleId);
                            return role ? employeeRoleLabel(role.name) : '';
                        }),
                        catchError(() => of(''))
                    );
                }),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((roleName) => (this.roleName = roleName));

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    toggleColorScheme(): void {
        this.appearance.setMode(this.isDark ? 'light' : 'dark');
    }

    private _attachNavigationFunctions(items: FuseNavigationItem[]): void {
        items.forEach((item) => {
            if (item.id === 'absence.request.open') {
                item.function = () => this._openAbsenceRequestDialog();
            }

            if (item.children?.length) {
                this._attachNavigationFunctions(item.children);
            }
        });
    }

    private _openAbsenceRequestDialog(): void {
        this._dialog.open(AbsenceRequestDialogComponent, {
            width: '620px',
            maxWidth: '95vw',
            maxHeight: '88dvh',
            panelClass: 'absence-entry-dialog',
            data: {
                showEmployeeSelect: false,
                employeeId: this.user?.employeeId,
            },
        });
    }

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }
}
